import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { gettext, translationPropType } from '@eventbrite/i18n';
import { Link } from '@eventbrite/eds-link';
import { Icon } from '@eventbrite/eds-icon';
import { HelpChunky } from '@eventbrite/eds-iconography';
import './guidance.scss';

const DEFAULT_TEXT_CONTENT = gettext('Learn more about');

export default class Guidance extends PureComponent {
    static propTypes = {
        /**
         * Primary copy to display in component
         */
        textContent: translationPropType,
        /**
         * Left aligned icon
         */
        primaryIcon: PropTypes.node,
        /**
         * Configuration for the link. {linkText:TranslatedString, linkTo:String, onLinkClick:Function, linkIcon:Icon}
         */
        linkConfig: PropTypes.shape({
            /**
             * Text content for link
             */
            linkText: translationPropType.isRequired,
            /**
             * Text content for link
             */
            linkTo: PropTypes.string.isRequired,
            /**
             * Click handler for link
             */
            onLinkClick: PropTypes.func,
            /**
             * Icon to display right aligned in the pill
             */
            linkIcon: PropTypes.node,
        }).isRequired,
    };

    static defaultProps = {
        primaryIcon: <Icon type={<HelpChunky />} />,
        textContent: DEFAULT_TEXT_CONTENT,
    };

    _handleDangerouslySetLinkAction = (e) => {
        // Event handler is not fired on any element used as string inside dangerouslySetInnerHTML
        // We instead leverage event delegation to properly handle the link interactions
        if (
            e.target.className === 'eds-guidance__content-link' ||
            e.keycode === 13
        ) {
            this._handleLinkClick();
        }
    };

    _handleLinkClick = () => {
        const { linkConfig } = this.props;

        if (linkConfig.onLinkClick) {
            linkConfig.onLinkClick();
        }
    };

    _renderPrimaryIcon = () => {
        const { primaryIcon } = this.props;
        const __containerClassName = 'eds-guidance__icon';

        let component = null;

        if (primaryIcon) {
            // Apply additional props to inner <i> element of Icon
            component = React.cloneElement(
                this.props.primaryIcon,
                // Apply additional classes to inner <i> element of Icon
                {
                    __containerClassName,
                    size: 'small',
                },
            );
        }

        return component;
    };

    _getLinkIcon = () => {
        const { linkConfig, textContent } = this.props;
        let component = null;

        if (linkConfig && linkConfig.linkIcon) {
            // Apply additional props to Icon
            const icon = React.cloneElement(linkConfig.linkIcon, {
                size: 'xsmall',
                color: 'ui-blue',
            });

            component = (
                <Link
                    to={linkConfig.linkTo}
                    onClick={this._handleLinkClick}
                    target="_blank"
                >
                    {icon}
                    <span
                        className="eds-is-hidden-accessible"
                        data-spec="eds-is-hidden-accessible"
                    >
                        {linkConfig.linkText || textContent}
                    </span>
                </Link>
            );
        }

        return component;
    };

    _renderContent = () => {
        const { linkConfig, textContent } = this.props;
        const { linkText, linkTo } = linkConfig;
        const content = gettext(
            '<span    className="eds-guidance__content-copy"    data-spec="eds-guidance__content-copy">    %(textContent)s</span>',
            { textContent },
        ).toString();
        const link = gettext(
            '<a    href=%(linkTo)s    target="_blank"    class="eds-guidance__content-link">    %(linkText)s</a>',
            { linkTo, linkText },
        ).toString();
        const __html = linkText ? content + link : content;
        const translatedContent = { __html };

        return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={translatedContent}
                data-spec="eds-guidance__content--translated"
                onClick={this._handleDangerouslySetLinkAction}
                onKeyDown={this._handleDangerouslySetLinkAction}
                role="contentinfo"
            />
        );
    };

    render() {
        return (
            <div className="eds-guidance">
                {this._renderPrimaryIcon()}
                <div
                    className="eds-guidance__content"
                    data-spec="eds-guidance__content"
                >
                    {this._renderContent()}
                    {this._getLinkIcon()}
                </div>
            </div>
        );
    }
}
